import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useProductStore } from '../store/productStore'
import { getProductMedia } from '../types'
import { ArrowRight, Plus, X } from 'lucide-react'
import { unitLabels } from '../lib/catalogConfig'
import StatusBadge from '../components/ui/StatusBadge'
import Carousel from '../features/product/Carousel'
import ProductInfoTable from '../features/product/ProductInfoTable'
import { useFavorites } from '../hooks/useFavorites'


export default function ProductPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { products } = useProductStore()
  const { isFavorite, toggle: toggleFavorite } = useFavorites()
  const product = products.find(p => p.id === id) || null

  useEffect(() => { window.scrollTo(0, 0) }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Backspace') { e.preventDefault(); navigate('/') }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [navigate])

  useEffect(() => {
    localStorage.setItem('imarket_banner_dismissed', 'true')
  }, [])

  if (!product) return (
    <div dir="rtl" className="min-h-screen flex items-center justify-center bg-cream">
      <p className="text-text-muted text-base">מוצר לא נמצא</p>
    </div>
  )

  const alreadyInList = isFavorite(product.id)
  const media = getProductMedia(product)

  return (
    <div dir="rtl" className="min-h-screen bg-cream">
      <div
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse 70% 50% at 95% 5%, rgba(82,183,136,0.10) 0%, transparent 60%),
            radial-gradient(ellipse 50% 40% at 5% 90%, rgba(244,132,95,0.08) 0%, transparent 55%)
          `
        }}
      />

      <main className="relative z-10 max-w-7xl mx-auto px-6 pt-4 pb-16">
        <button
          onClick={() => navigate(-1)}
          aria-label="חזרה לקטלוג"
          className="flex items-center gap-2 bg-white border-none cursor-pointer px-4 py-2 rounded-[10px] text-sm font-medium text-green-primary shadow-[0_2px_8px_rgba(44,24,16,0.07)] my-6 transition-shadow hover:shadow-[0_4px_16px_rgba(44,24,16,0.12)]"
        >
          <ArrowRight className="h-4 w-4" />
          חזרה לקטלוג
        </button>

        <article className="bg-white rounded-[24px] border border-cream-dark overflow-hidden hover:shadow-[0_6px_24px_rgba(44,24,16,0.10)] duration-500">
          <div className="relative">
            <Carousel media={media} name={product.name} />
            <span className="absolute top-4 left-4 z-10">
              <StatusBadge status={product.status} />
            </span>
          </div>

          <section className="px-8 pt-0 pb-8">
            <h1 className="font-serif text-[28px] font-bold text-text-primary tracking-tight mb-2">
              {product.name}
            </h1>
            {product.description && (
              <p className="text-[15px] text-text-secondary leading-relaxed mb-7">
                {product.description}
              </p>
            )}

            <ProductInfoTable product={product} />

            <div className="flex items-center justify-between">
              <span className="font-serif text-[32px] font-bold text-green-primary">
                <span>₪</span>
                <span className="ml-1">{product.price}</span>
                <span className="text-base font-normal text-text-muted font-sans"> / {unitLabels[product.unit]}</span>
              </span>
              <div className="flex items-center gap-3">
              {(product.status === 'active' || product.status === 'sale') && (
                  <button
                    onClick={() => { toggleFavorite(product.id); if (!alreadyInList) navigate('/') }}
                    className={`px-5 py-3 rounded-full border-none text-[14px] font-semibold cursor-pointer
                      shadow-[0_4px_14px_rgba(44,24,16,0.20)] transition-all flex items-center gap-2 whitespace-nowrap
                      ${alreadyInList
                        ? 'bg-[#FFE8E0] text-[#C0392B] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(231,76,60,0.25)]'
                        : 'bg-[#2D6A4F] text-white hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(45,106,79,0.35)]'
                      }`}
                  >
                    {alreadyInList
                      ? <><X className="w-4 h-4" /> הסר מהרשימה</>
                      : <>הוסף לרשימה <Plus className="w-4 h-4" /></>
                    }
                  </button>
                )}
              </div>
            </div>
          </section>
        </article>
      </main>
    </div>
  )
}